import React from 'react';
import type { TeamChatMessage as TeamChatMessageType } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface Props {
  message: TeamChatMessageType;
  isOwn: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const TeamChatMessage: React.FC<Props> = ({ message, isOwn, isFirst = true, isLast = true }) => {
  // Bubble border radius: same corner shape as widget
  // Own (right): top-left top-right bottom-right bottom-left
  //   → 12 12 2 12 (sharp bottom-right = tail side)
  // Other (left): 12 12 12 2 (sharp bottom-left = tail side)
  const bubbleRadius = isOwn ? '12px 12px 2px 12px' : '12px 12px 12px 2px';

  const bubbleStyle: React.CSSProperties = {
    maxWidth: '78%',
    borderRadius: bubbleRadius,
    padding: '8px 12px',
    backgroundColor: isOwn ? '#8b5cf6' : '#ffffff',
    color: isOwn ? '#ffffff' : '#1e293b',
    alignSelf: isOwn ? 'flex-end' : 'flex-start',
    wordBreak: 'break-word',
    ...(isOwn
      ? {}
      : {
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          border: '1px solid #f3f4f6',
        }),
  };

  const timeStyle: React.CSSProperties = {
    fontSize: 10,
    color: '#94a3b8',
    marginTop: 2,
    textAlign: isOwn ? 'right' : 'left',
  };

  const renderContent = () => {
    if (message.type === 'text') {
      return <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>{message.content}</p>;
    }

    if (message.type === 'image' && message.mediaUrl) {
      return (
        <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={message.mediaUrl}
            alt={message.mediaName || 'Imagem'}
            style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, display: 'block' }}
          />
        </a>
      );
    }

    if (message.type === 'audio' && message.mediaUrl) {
      return (
        <audio controls style={{ maxWidth: '100%', height: 36 }}>
          <source src={message.mediaUrl} type={message.mediaMimeType} />
        </audio>
      );
    }

    if (message.type === 'file' && message.mediaUrl) {
      return (
        <a
          href={message.mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            color: isOwn ? '#ffffff' : '#1e293b',
          }}
        >
          <Icon name="file" size={20} style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 500, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {message.mediaName || 'Arquivo'}
            </p>
            {message.mediaSize && (
              <p style={{ fontSize: 10, margin: 0, opacity: 0.7 }}>{formatFileSize(message.mediaSize)}</p>
            )}
          </div>
          <Icon name="download" size={14} style={{ flexShrink: 0, marginLeft: 'auto' }} />
        </a>
      );
    }

    return null;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        marginTop: isFirst ? 6 : 1,
      }}
    >
      <div style={bubbleStyle}>{renderContent()}</div>
      {isLast && <span style={timeStyle}>{formatTime(message.createdAt)}</span>}
    </div>
  );
};
